import React, {useCallback, useEffect, useState} from 'react'
import {Box} from '@mui/material'
import DataTable, {ColumnDef, PaginationState} from 'src/components-new/data/DataTable'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import FilePreviewDialog from 'src/components-new/feedback/FilePreviewDialog'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'
import Button from 'src/components-new/primitives/Button'
import {STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import {saveBlob} from 'src/utils/medicalReportFile'
import {buildColumns, EchoRequestRow, getActiveColumnIds, STATE_OPTIONS} from './echocardiographicRequestsColumns'
import EchocardiographicRequestDialogNew, {EchoRequestFormValues, EXAM_TYPE_OPTIONS} from './EchocardiographicRequestDialogNew'

// Base set of filter fields (not PSP-driven) — mirrors the legacy page's filterConfig, see
// src/pages/EchocardiographicRequests/EchocardiographicRequests.tsx lines 90-113. Unlike
// biologicalSamples, this section's filters aren't read from PSP config directly — but each
// one is still tied to a column below via FILTER_COLUMN_ID, so a filter never outlives the
// column it searches into.
const FILTER_CONFIG: FilterItem[] = [
  {id: 'patient', label: 'Paziente', type: 'patientLookup'},
  {id: 'examType', label: 'Tipo esame', type: 'select', options: EXAM_TYPE_OPTIONS},
  {id: 'rangeDates', label: 'Data', type: 'rangePicker'},
  {id: 'state', label: 'Stato', type: 'select', options: STATE_OPTIONS.filter(opt => opt.value !== 'CANCELLED')},
]

// Maps a filter's id to the column id it depends on, for the one filter whose id doesn't
// match its column 1:1 (the "Data" filter searches on `rangeDates`, but the table only ever
// has a `createdAt` column). Every other filter id already equals its column id.
const FILTER_COLUMN_ID: Record<string, string> = {
  rangeDates: 'createdAt',
}

const REPORT_FILE_NAME = 'referto.pdf'
const REPORT_MIME = 'application/pdf'
const INITIAL_VIEWER_STATE = {open: false, filePath: '', fileName: REPORT_FILE_NAME, status: STATUS.PENDING}

/**
 * New-style equivalent of src/pages/EchocardiographicRequests/EchocardiographicRequests.tsx.
 * No page title/breadcrumb is rendered here — HeaderBar derives it from the
 * "echocardiographicRequests" nav item label — and there is no row click / full detail
 * page, matching the legacy behavior (only the two dedicated row actions).
 */
export default function EchocardiographicRequestsListNew(): JSX.Element {
  const columnOptions = useAppSelector(
    state => state.pspConfigReducer.data.echocardiographicRequestsConfigurations?.columnOptions
  )
  const toast = useToast()

  const [rows, setRows] = useState<EchoRequestRow[]>([])
  const [count, setCount] = useState<number>(0)
  const [status, setStatus] = useState<string>(STATUS.PENDING)
  const [statusCount, setStatusCount] = useState<string>(STATUS.PENDING)
  const [statusDownload, setStatusDownload] = useState<string | undefined>()
  const [activeFilters, setActiveFilters] = useState<FilterResult[]>([])
  const [pagination, setPagination] = useState<PaginationState>({page: 0, rowsPerPage: 10, order: '-createdAt'})
  const [requestOpen, setRequestOpen] = useState(false)
  const [detailRow, setDetailRow] = useState<EchoRequestRow | undefined>()
  const [viewerState, setViewerState] = useState(INITIAL_VIEWER_STATE)

  const fetchData = useCallback(() => {
    Promise.all([
      apiClient.getExamsRequests(activeFilters, setStatus, pagination),
      apiClient.getExamsRequestsCount(activeFilters, setStatusCount),
    ])
      .then(([data, countResult]) => {
        if (Array.isArray(data)) {
          setRows(data.map((row: any) => ({
            ...row,
            patient: `${row.patient?.firstName ?? ''} ${row.patient?.lastName ?? ''}`.trim(),
          })))
        }
        const n = Number(countResult)
        if (!isNaN(n)) setCount(n)
      })
      .catch(() => setStatus(STATUS.REJECTED))
  }, [activeFilters, pagination])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    if (statusDownload === STATUS.REJECTED) setViewerState(INITIAL_VIEWER_STATE)
  }, [statusDownload])

  const handleFiltersSubmit = (values: FilterResult[]) => {
    setActiveFilters(values)
    setPagination(prev => ({...prev, page: 0}))
  }

  // `examType` on the backend is a single string, so a multi-select in the dialog
  // translates into one POST per selected type. Requests are fired sequentially
  // (rather than Promise.all) so a slow/failing one doesn't race the others, and all
  // of them are attempted even if one fails, then a single summary is reported.
  const handleSaveRequest = async ({title, examTypes, description, patient}: EchoRequestFormValues) => {
    const results: {examType: string; success: boolean}[] = []
    for (const examType of examTypes) {
      let finalStatus: string | undefined
      // eslint-disable-next-line no-await-in-loop
      await apiClient.postExamRequest({title, examType, description, user: patient}, (s: string) => { finalStatus = s })
      results.push({examType, success: finalStatus === STATUS.FULFILLED})
    }

    const failed = results.filter(r => !r.success)
    const examTypeLabel = (value: string) => EXAM_TYPE_OPTIONS.find(opt => opt.value === value)?.label ?? value

    if (failed.length === 0) {
      toast.success(results.length > 1 ? 'Richieste inviate con successo.' : 'Richiesta inviata con successo.')
      setRequestOpen(false)
      fetchData()
    } else if (failed.length === results.length) {
      toast.error("Errore nella richiesta dell'esame.")
    } else {
      toast.warning(
        `Alcune richieste non sono andate a buon fine: ${failed.map(f => examTypeLabel(f.examType)).join(', ')}.`
      )
      setRequestOpen(false)
      fetchData()
    }
  }

  const handleDownloadReport = async (row: EchoRequestRow) => {
    setViewerState({open: true, filePath: '', fileName: REPORT_FILE_NAME, status: STATUS.PENDING})
    const report = await apiClient.downloadMedicalReport(row.patientId as string, row.examId as string, setStatusDownload)
    if (!report) return
    // Solo i PDF hanno l'anteprima: gli altri formati si scaricano con il nome originale
    if (!report.isPdf) {
      setViewerState(INITIAL_VIEWER_STATE)
      saveBlob(report.blob, report.fileName)
      return
    }
    const url = (window.URL || window.webkitURL).createObjectURL(report.blob)
    setViewerState({open: true, filePath: url, fileName: REPORT_FILE_NAME, status: STATUS.FULFILLED})
  }

  // Only offer a filter if its column is actually shown — e.g. if a PSP config disables
  // the "Tipo esame" column, the "Tipo esame" filter disappears from the drawer too, rather
  // than letting a PSP admin filter on data the table never displays.
  const activeColumnIds = getActiveColumnIds(columnOptions)
  const filterConfig = FILTER_CONFIG.filter(item => activeColumnIds.has(FILTER_COLUMN_ID[item.id] ?? item.id))

  const columns: ColumnDef<EchoRequestRow>[] = [
    ...buildColumns(columnOptions),
    {
      key: 'actions',
      header: '',
      width: 80,
      isActionColumn: true,
      render: (row: EchoRequestRow) => (
        <Box onClick={(e) => e.stopPropagation()} sx={{display: 'flex', gap: 0.5}}>
          {row.examId && (
            <ActionIconButton onClick={() => handleDownloadReport(row)} tooltip="Scarica referto" variant="download" />
          )}
          <ActionIconButton onClick={() => setDetailRow(row)} tooltip="Dettaglio" variant="view" />
        </Box>
      ),
    },
  ]

  return (
    <Box>
      <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2, flexWrap: 'wrap'}}>
        <Button onClick={() => setRequestOpen(true)} variant="primary">
          {'Richiedi'}
        </Button>
        <Filters config={filterConfig} onSubmit={handleFiltersSubmit} />
      </Box>

      {(status === STATUS.REJECTED || statusCount === STATUS.REJECTED) && (
        <InlineMessage
          severity="error"
          onClose={() => { setStatus(STATUS.FULFILLED); setStatusCount(STATUS.FULFILLED) }}
          sx={{mb: 2}}
        >
          {'Errore nel caricare i dati della pagina.'}
        </InlineMessage>
      )}

      {statusDownload === STATUS.REJECTED && (
        <InlineMessage severity="error" onClose={() => setStatusDownload(undefined)} sx={{mb: 2}}>
          {'Errore nello scaricare il referto.'}
        </InlineMessage>
      )}

      <DataTable<EchoRequestRow>
        columns={columns}
        rows={rows}
        keyField="_id"
        loading={status === STATUS.PENDING || statusCount === STATUS.PENDING}
        emptyMessage="Nessuna richiesta trovata"
        totalElements={count}
        pagination={pagination}
        onPaginationChange={setPagination}
      />

      {requestOpen && (
        <EchocardiographicRequestDialogNew
          onClose={() => setRequestOpen(false)}
          onSave={handleSaveRequest}
          open={requestOpen}
        />
      )}

      {detailRow && (
        <EchocardiographicRequestDialogNew
          initialValues={detailRow}
          onClose={() => setDetailRow(undefined)}
          onSave={() => undefined}
          open
          readOnly
        />
      )}

      <FilePreviewDialog
        fileName={viewerState.fileName}
        filePath={viewerState.filePath}
        mime={REPORT_MIME}
        onClose={() => setViewerState(INITIAL_VIEWER_STATE)}
        open={viewerState.open}
        status={viewerState.status}
      />
    </Box>
  )
}